export const ASSISTANT_INSIGHTS_QUERY_FLAG = 'VITE_ASSISTANT_ENABLE_INSIGHTS_QUERY'
export const INSIGHTS_QUERY_TOOL_NAME = 'run-insights-query'
export const TEST_INSIGHTS_API_URL = 'https://api.shared-platform.eidu.com/test-insights'

export const INSIGHTS_SCHEMA_REFERENCE = `Available tables (maintain this list manually and keep it up to date):

- Schema: schools
  - Table: school_details
      // Ids
      - Column: organization_id             // The organization the school belongs to
      - Column: school_id                   // The internal id of the school
      - Column: school_entity_id            // The id within the console of the school
      - Column: name                        // The school's name

      // Classifications, Cohorts
      - Column: school_type                 // Public or Private
      - Column: is_valid_school             // Bool - if not explicitly said otherwise filter for valid schools
      - Column: townhall_id                 // In townhall meetings, schools are added to the system
      - Column: program                     // The implementation model like TayariDpl, DplPublic the school uses

      // Location levels of administrative hierarchy
      - Column: country
      - Column: county
      - Column: subcounty
      - Column: zone

      // Physical location of the school
      - Column: longitude
      - Column: latitude
      - Column: location_source

      // Creation date
      - Column: time_created

  - Table: school_dayseries
    - Column: school_id
    - Column: usage_day
    - Column: lifetime_day                  // number of days since the addition to the system
    - Column: is_term_weekday               // true if weekday (not Saturday or Sunday) during a term (not in term breaks)
    - Column: usage_hours                   // DPL usage on that day (in hours)
    - Column: usage_minutes                 // DPL usage on that day (in minutes)
    - Column: device_count                  // number of devices in the school

- Schema: classes
  - Table: class_details
      - Column: class_id
      - Column: school_id
  - Table: class_dayseries
    - Column: class_id
    - Column: school_id
    - Column: usage_day
    - Column: usage_hours

- Schema: learners
  - Table: learner_details
    - Column: learner_id
    - Column: class_id
    - Column: school_id
  - Table: learner_dayseries
    - Column: learner_id
    - Column: class_id
    - Column: school_id
    - Column: usage_day
    - Column: usage_hours

- Schema: devices
  - Table: device_details
    - Column: android_id
    - Column: school_id
  - Table: device_dayseries
    - Column: android_id
    - Column: school_id
    - Column: usage_day
    - Column: usage_hours`

export const DATASET_SYSTEM_PROMPT = `You help authors define YAML assets for analytics datasets that run against the insights service.

Output rules:
- Respond with valid YAML only (no prose), using:
  - \`query\`: required SQL SELECT template
  - \`params\`: optional array of param objects
- The \`query\` value must be a SQL SELECT suitable for Redshift (no writes, no DDL).
- Query placeholders must use only param names: \`{{ParamName}}\`.
- Param object shape:
  - \`name\`: param name used in \`{{...}}\`
  - \`target\`: optional SQL target expression, defaults to \`name\`; use \`null\` for raw-value mode
  - \`type\`: \`"string"\` (default) or \`"integer"\`
  - \`default\` (or \`defaultValue\`): optional scalar or array
- Replacement behavior:
  - If \`target\` is a non-empty string: render predicate form (\`target = value\` or \`target in (...)\`).
  - If \`target\` is \`null\`: render only formatted value(s), with no auto-added \`target\`, \`=\`, or \`in (...)\`.
- String values are SQL-quoted/escaped; integer values must be valid integers.
- The insights service enforces its own row cap.
- Use fully qualified columns and clear aliases when helpful.
- Prefer concise, efficient queries that return just the necessary columns.
- Use the shared insights schema reference documented in the MCP tool description for \`${INSIGHTS_QUERY_TOOL_NAME}\` instead of guessing tables or columns.
- If information is missing or a query cannot be produced safely, return output equal to the input and describe the missing schema detail in the user's reply.
- Don't add unnecessary schema or table qualifiers.`

export const INSIGHTS_QUERY_TOOL_DESCRIPTION = `Run a read-only ad-hoc SQL query against the insights service in the test environment.

Usage rules:
- Include a short user-facing summary in \`query\`.
- Put the actual SQL in \`sql\`.
- Only read-only SQL is allowed: \`SELECT ...\` or \`WITH ... SELECT ...\`.
- Multi-statement SQL is rejected.
- Results are capped at 1000 rows and may be truncated further by a payload-size guard.
- The response includes the executed SQL, fields, rows, and truncation metadata.
- This tool is experimental and only available in test.
- The documented tables below are a manual schema reference for common analytics queries. Other readable tables may exist, but do not invent unknown columns.

${INSIGHTS_SCHEMA_REFERENCE}`

const normalizeBooleanFlag = (value: string | boolean | null | undefined) => {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on'
}

export const isInsightsQueryEnabled = ({ stage, flag }: { stage?: string | null; flag?: string | boolean | null }) =>
  stage?.trim().toLowerCase() === 'test' && normalizeBooleanFlag(flag)
